import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import './FeedbackPage.css';

const FeedbackPage = () => {
  const { currentUser } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [showNewFeedbackForm, setShowNewFeedbackForm] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingFeedbacks, setDeletingFeedbacks] = useState(new Set());
  const [expandedImages, setExpandedImages] = useState(new Set());
  
  // Form state
  const [newFeedback, setNewFeedback] = useState({
    title: '',
    message: '',
    image: null
  });
  
  const fileInputRef = useRef(null);

  // Dynamic header/footer height calculation
  useEffect(() => {
    const calculateHeaderFooterHeight = () => {
      const header = document.querySelector('.header');
      const footer = document.querySelector('.footer');
      
      if (header && footer) {
        const headerHeight = header.offsetHeight;
        const footerHeight = footer.offsetHeight;
        const totalHeight = headerHeight + footerHeight;
        
        document.documentElement.style.setProperty('--header-footer-height', `${totalHeight}px`);
      }
    };

    calculateHeaderFooterHeight();
    const timeoutId = setTimeout(calculateHeaderFooterHeight, 100);
    
    const handleResize = () => {
      calculateHeaderFooterHeight();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Fetch feedbacks
  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/feedbacks', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch feedbacks');
      }
      
      const data = await response.json();
      setFeedbacks(data);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setError('Failed to load feedbacks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchFeedbacks();
    }
  }, [currentUser]);

  // Handle new feedback form
  const handleShowNewFeedbackForm = () => {
    setShowNewFeedbackForm(true);
    setNewFeedback({ title: '', message: '', image: null });
  };

  const handleCancelNewFeedback = () => {
    setShowNewFeedbackForm(false);
    setNewFeedback({ title: '', message: '', image: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setStatusMessage('Image file is too large. Please select an image smaller than 10MB.');
        setTimeout(() => setStatusMessage(''), 5000);
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setStatusMessage('Please select a valid image file.');
        setTimeout(() => setStatusMessage(''), 5000);
        return;
      }
      
      setNewFeedback(prev => ({ ...prev, image: file }));
    }
  };

  const handleSubmitFeedback = async () => {
    if (!newFeedback.title.trim() || !newFeedback.message.trim()) {
      setStatusMessage('Please fill in both title and message.');
      setTimeout(() => setStatusMessage(''), 3000);
      return;
    }

    try {
      setSubmittingFeedback(true);
      
      // First, create the feedback
      const feedbackResponse = await fetch('/api/feedbacks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: newFeedback.title.trim(),
          message: newFeedback.message.trim()
        })
      });

      if (!feedbackResponse.ok) {
        throw new Error('Failed to submit feedback');
      }

      const feedbackData = await feedbackResponse.json();
      const feedbackId = feedbackData.feedback.feedback_id;

      // If there's an image, upload it
      if (newFeedback.image) {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', newFeedback.image);
        formData.append('feedbackId', feedbackId);

        const imageResponse = await fetch('/api/feedbacks/upload-image', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        if (!imageResponse.ok) {
          console.warn('Failed to upload image, but feedback was created');
        }
      }

      setStatusMessage('Feedback submitted successfully! Thank you for your input.');
      setTimeout(() => setStatusMessage(''), 3000);
      
      // Reset form and refresh feedbacks
      handleCancelNewFeedback();
      await fetchFeedbacks();
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setStatusMessage('Failed to submit feedback. Please try again.');
      setTimeout(() => setStatusMessage(''), 5000);
    } finally {
      setSubmittingFeedback(false);
      setUploadingImage(false);
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    setDeletingFeedbacks(prev => new Set([...prev, feedbackId]));
    
    try {
      const response = await fetch(`/api/feedbacks/${feedbackId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete feedback');
      }

      setStatusMessage('Feedback deleted successfully.');
      setTimeout(() => setStatusMessage(''), 3000);
      await fetchFeedbacks();
      
    } catch (error) {
      console.error('Error deleting feedback:', error);
      setStatusMessage('Failed to delete feedback. Please try again.');
      setTimeout(() => setStatusMessage(''), 5000);
    } finally {
      setDeletingFeedbacks(prev => {
        const newSet = new Set(prev);
        newSet.delete(feedbackId);
        return newSet;
      });
    }
  };

  const handleToggleImageExpand = (feedbackId) => {
    setExpandedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(feedbackId)) {
        newSet.delete(feedbackId);
      } else {
        newSet.add(feedbackId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'reviewed': return '#3498db';
      case 'resolved': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending Review';
      case 'reviewed': return 'Under Review';
      case 'resolved': return 'Resolved';
      default: return 'Unknown';
    }
  };

  if (!currentUser) {
    return (
      <div className="feedback-page">
        <div className="feedback-container">
          <h1>Please log in to submit feedback</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-page">
      <div className="feedback-container">
        <div className="feedback-header">
          <h1><i className="fas fa-comments"></i> We're Always Happy to Hear From You</h1>
          <div className="user-info">
            <h2>Tell us what you think, {currentUser.username}!</h2>
          </div>
        </div>

        <div className="feedback-content">
          <div className="feedbacks-section">
            <div className="section-header">
              <h3><i className="fas fa-list"></i> Your Feedbacks ({feedbacks.length})</h3>
              <button 
                className="add-feedback-btn" 
                onClick={handleShowNewFeedbackForm}
                title="Add new feedback"
                style={{
                  background: 'var(--primary-color)',
                  color: 'var(--text-color)',
                  border: '2px solid var(--primary-color)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-primary)',
                  fontWeight: '600',
                  fontSize: 'var(--font-size-sm)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--secondary-color)';
                  e.target.style.borderColor = 'var(--secondary-color)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'var(--primary-color)';
                  e.target.style.borderColor = 'var(--primary-color)';
                }}
              >
                <i className="fas fa-plus"></i>
                Add New Feedback
              </button>
            </div>

            {/* New Feedback Form */}
            {showNewFeedbackForm && (
              <div className="new-feedback-form">
                <div className="form-header">
                  <h4><i className="fas fa-edit"></i> Share Your Feedback</h4>
                  <button 
                    className="close-form-btn"
                    onClick={handleCancelNewFeedback}
                    title="Cancel"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                
                <div className="form-content">
                  <div className="form-group">
                    <label htmlFor="feedback-title">Title:</label>
                    <input
                      id="feedback-title"
                      type="text"
                      value={newFeedback.title}
                      onChange={(e) => setNewFeedback(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Brief description of your feedback..."
                      maxLength={255}
                      className="feedback-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="feedback-message">Message:</label>
                    <textarea
                      id="feedback-message"
                      value={newFeedback.message}
                      onChange={(e) => setNewFeedback(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Tell us more about your thoughts, suggestions, or issues..."
                      rows={5}
                      className="feedback-textarea"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="feedback-image">Attach Image (optional):</label>
                    <div className="image-upload-area">
                      <input
                        ref={fileInputRef}
                        id="feedback-image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="feedback-file-input"
                      />
                      {newFeedback.image && (
                        <div className="selected-image">
                          <i className="fas fa-image"></i>
                          <span>{newFeedback.image.name}</span>
                          <button 
                            type="button"
                            onClick={() => {
                              setNewFeedback(prev => ({ ...prev, image: null }));
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                            className="remove-image-btn"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-actions">
                    <button 
                      className="submit-feedback-btn"
                      onClick={handleSubmitFeedback}
                      disabled={submittingFeedback || uploadingImage}
                    >
                      {submittingFeedback ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          {uploadingImage ? 'Uploading...' : 'Submitting...'}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          Submit Feedback
                        </>
                      )}
                    </button>
                    
                    <button 
                      className="cancel-feedback-btn"
                      onClick={handleCancelNewFeedback}
                      disabled={submittingFeedback}
                    >
                      <i className="fas fa-times"></i>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {loading && <div className="loading">Loading feedbacks...</div>}
            {error && <div className="error">Error: {error}</div>}

            {feedbacks.length === 0 && !loading ? (
              <div className="no-feedbacks">
                <p>No feedback submitted yet.</p>
                <p>Share your thoughts and help us improve!</p>
              </div>
            ) : (
              <div className="feedbacks-grid">
                {feedbacks.map((feedback) => (
                  <div key={feedback.feedback_id} className="feedback-card">
                    <div className="feedback-content">
                      <div className="feedback-info-column">
                        <div className="feedback-top-row">
                          <h4 className="feedback-title">
                            {feedback.title}
                          </h4>
                          <div className="feedback-actions">
                            <div 
                              className="status-badge"
                              style={{ 
                                backgroundColor: getStatusColor(feedback.status),
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                fontWeight: '600'
                              }}
                            >
                              {getStatusText(feedback.status)}
                            </div>
                            <button 
                              onClick={() => handleDeleteFeedback(feedback.feedback_id)}
                              className="delete-btn"
                              title={deletingFeedbacks.has(feedback.feedback_id) ? "Deleting..." : "Delete Feedback"}
                              disabled={deletingFeedbacks.has(feedback.feedback_id)}
                            >
                              {deletingFeedbacks.has(feedback.feedback_id) ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-trash"></i>
                              )}
                            </button>
                          </div>
                        </div>
                        
                        <div className="feedback-bottom-row">
                          <p className="feedback-message">{feedback.message}</p>
                          
                          <div className="feedback-details">
                            <div className="info-row" title="Submitted Date">
                              <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                                <path d="M21 3v5h-5"/>
                              </svg>
                              <span className="value">{formatDate(feedback.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {feedback.image_url && (
                        <div 
                          className={`feedback-image-column ${expandedImages.has(feedback.feedback_id) ? 'expanded' : ''}`}
                          onClick={() => handleToggleImageExpand(feedback.feedback_id)}
                        >
                          <div 
                            className="feedback-image-placeholder"
                            style={{ backgroundImage: `url(${feedback.image_url})` }}
                          >
                            <div className="image-overlay">
                              <span>Click to {expandedImages.has(feedback.feedback_id) ? 'collapse' : 'expand'}</span>
                            </div>
                            {!expandedImages.has(feedback.feedback_id) && (
                              <div className="expand-hint">
                                <i className="fas fa-expand"></i>
                              </div>
                            )}
                            {expandedImages.has(feedback.feedback_id) && (
                              <div className="collapse-hint">
                                <i className="fas fa-times"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div className="status-message">
            {statusMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackPage; 